import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { FileText, Plus, Search } from 'lucide-react'
import { offerten as offertenApi } from '../../lib/api'
import { TopBar } from '../../components/layout/TopBar'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { offertStatusBadge } from '../../components/ui/Badge'
import { EmptyState, LoadingSpinner } from '../../components/ui/EmptyState'
import { formatDatum } from '../../lib/utils'
import { cn } from '../../lib/utils'

export function OffertenListePage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('alle')

  const { data, isLoading } = useQuery({ queryKey: ['offerten'], queryFn: () => offertenApi.list() })

  const filtered = (data ?? []).filter((o) => {
    const matchSearch = [o.nummer, o.kunde_name, o.ersteller].some((v) => v?.toLowerCase().includes(search.toLowerCase()))
    const matchStatus = statusFilter === 'alle' || o.status === statusFilter
    return matchSearch && matchStatus
  })

  return (
    <>
      <TopBar title="Offerten"
        rightAction={<Button icon={<Plus size={18} />} size="sm" onClick={() => navigate('/offerten/neu')}>Neue Offerte</Button>}
      />
      <div className="px-4 py-3 space-y-3">
        <Input placeholder="Suche..." value={search} onChange={(e) => setSearch(e.target.value)} icon={<Search size={16} />} />
        <div className="flex gap-2 overflow-x-auto scrollbar-none">
          {[
            { v: 'alle', l: 'Alle' }, { v: 'entwurf', l: 'Entwurf' }, { v: 'gesendet', l: 'Gesendet' },
            { v: 'angenommen', l: 'Angenommen' }, { v: 'abgelehnt', l: 'Abgelehnt' },
          ].map(({ v, l }) => (
            <button key={v} onClick={() => setStatusFilter(v)}
              className={cn('shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-colors',
                statusFilter === v ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600'
              )}>
              {l}
            </button>
          ))}
        </div>

        {isLoading ? <LoadingSpinner /> : filtered.length === 0 ? (
          <EmptyState icon={<FileText size={28} />} title="Keine Offerten"
            action={<Button icon={<Plus size={16} />} onClick={() => navigate('/offerten/neu')}>Neue Offerte</Button>}
          />
        ) : (
          <div className="space-y-2">
            {filtered.map((o) => (
              <Card key={o.id} clickable onClick={() => navigate(`/offerten/${o.id}`)} className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center shrink-0">
                  <FileText size={18} className="text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-sm text-gray-900">{o.nummer}</p>
                    {offertStatusBadge(o.status)}
                  </div>
                  <p className="text-xs text-gray-600 truncate">{o.kunde_name}</p>
                  <p className="text-xs text-gray-400">{o.ersteller} · {formatDatum(o.erstellt_am)}</p>
                </div>
                {o.gueltig_bis && (
                  <div className="text-right shrink-0">
                    <p className="text-xs text-gray-500">Gültig bis</p>
                    <p className="text-xs font-medium">{formatDatum(o.gueltig_bis)}</p>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
